import { Box, Typography } from '@mui/material';
import { numberToUSDCurrency } from '../../helpers/numberToUSDCurrency';
import { StatusChip } from '../../components/StatusChip';
import { AttorneysFeeStatusMap, DeductionStatusMap } from './SettlementsView/constants';
import { AttorneysFeeStatus, DeductionStatus } from '../../types';


interface Totals {
    [key: string]: number;
}

interface DeductionMapping {
    label: string;
    keys: { key: string; status: string }[];
}

interface AttorneysFeeMapping {
    label: string;
    keys: { key: string; status: string }[];
}

/**
 * Renders the totals into JSX rows
 */
export const renderTotals = (
    totals: Totals,
    classes: any, // eslint-disable-line @typescript-eslint/no-explicit-any
) => {
    const deductionMapping: DeductionMapping[] = [
        {
            label: 'Liens',
            keys: [
                { key: 'totalLienPaid', status: 'Paid' },
                { key: 'totalLienNotStarted', status: 'NotStarted' },
                { key: 'totalLienFailed', status: 'Failed' },
                { key: 'totalLienFunding', status: 'Funding' },
                { key: 'totalLienInDistribution', status: 'InDistribution' },
            ],
        },
        {
            label: 'Service Provider',
            keys: [
                { key: 'totalServiceProviderPaid', status: 'Paid' },
                { key: 'totalServiceProviderNotStarted', status: 'NotStarted' },
                { key: 'totalServiceProviderFailed', status: 'Failed' },
                { key: 'totalServiceProviderFunding', status: 'Funding' },
                { key: 'totalServiceProviderInDistribution', status: 'InDistribution' },
            ],
        },
        {
            label: 'Expenses',
            keys: [
                { key: 'totalExpensePaid', status: 'Paid' },
                { key: 'totalExpenseNotStarted', status: 'NotStarted' },
                { key: 'totalExpenseFailed', status: 'Failed' },
                { key: 'totalExpenseFunding', status: 'Funding' },
                { key: 'totalExpenseInDistribution', status: 'InDistribution' },
            ],
        },
    ];

    return deductionMapping.flatMap((section) =>
        section.keys
            .filter(({ key }) => totals[key] && totals[key] !== 0) // skip 0
            .map(({ key, status }) => {
                // Determine the count key
                let countKey = '';
                if (section.label === 'Liens') {
                    if (status === 'Paid') countKey = 'lienPaidCount';
                    else if (status === 'NotStarted') countKey = 'lienNotStartedCount';
                    else if (status === 'Failed') countKey = 'lienFailedCount';
                    else if (status === 'Funding') countKey = 'lienFundingCount';
                    else if (status === 'InDistribution') countKey = 'lienInDistributionCount';
                } else if (section.label === 'Service Provider') {
                    if (status === 'Paid') countKey = 'serviceProviderPaidCount';
                    else if (status === 'NotStarted') countKey = 'serviceProviderNotStartedCount';
                    else if (status === 'Failed') countKey = 'serviceProviderFailedCount';
                    else if (status === 'Funding') countKey = 'serviceProviderFundingCount';
                    else if (status === 'InDistribution') countKey = 'serviceProviderInDistributionCount';
                } else if (section.label === 'Expenses') {
                    if (status === 'Paid') countKey = 'expensePaidCount';
                    else if (status === 'NotStarted') countKey = 'expenseNotStartedCount';
                    else if (status === 'Failed') countKey = 'expenseFailedCount';
                    else if (status === 'Funding') countKey = 'expenseFundingCount';
                    else if (status === 'InDistribution') countKey = 'expenseInDistributionCount';
                }
                const count = totals[countKey] || 0;
                return (
                    <Box key={key} className={classes.row}>
                        <Typography className={classes.label}>
                            {section.label} - {count} items
                        </Typography>
                        <Typography className={classes.amountCenter}>
                            {numberToUSDCurrency(totals[key])}
                        </Typography>
                        {status &&
                            <Box className={classes.statusBadge}>
                                <StatusChip
                                    label={DeductionStatusMap[status as DeductionStatus].label || status}
                                    color={DeductionStatusMap[status as DeductionStatus].color || 'default'}
                                />
                            </Box>}
                    </Box>
                );
            })
    );
};

/**
 * Renders attorneys fees into JSX rows
 */
export const renderAttorneyFees = (
    attorneysFees: Record<string, any>, // eslint-disable-line @typescript-eslint/no-explicit-any
    classes: any, // eslint-disable-line @typescript-eslint/no-explicit-any
) => {
    if (!attorneysFees) return null;

    const attorneyMapping: AttorneysFeeMapping[] = [
        {
            label: 'Co Counsel',
            keys: [
                { key: 'secondaryFirmsCoCounselTotalPaid', status: 'Paid' },
                { key: 'secondaryFirmsCoCounselTotalNotStarted', status: 'NotStarted' },
                { key: 'secondaryFirmsCoCounselTotalFailed', status: 'Failed' },
                { key: 'secondaryFirmsCoCounselTotalFunding', status: 'Funding' },
                { key: 'secondaryFirmsCoCounselTotalInDistribution', status: 'InDistribution' },
            ],
        },
        {
            label: 'Referring Counsel',
            keys: [
                { key: 'secondaryFirmsReferingCounselTotalPaid', status: 'Paid' },
                { key: 'secondaryFirmsReferingCounselTotalNotStarted', status: 'NotStarted' },
                { key: 'secondaryFirmsReferingCounselTotalFailed', status: 'Failed' },
                { key: 'secondaryFirmsReferingCounselTotalFunding', status: 'Funding' },
                { key: 'secondaryFirmsReferingCounselTotalInDistribution', status: 'InDistribution' },
            ],
        },
    ];

    return attorneyMapping.flatMap((section) =>
        section.keys
            .filter(({ key }) => attorneysFees[key] && attorneysFees[key] !== 0) // skip 0
            .map(({ key, status }) => {
                // Determine the count key
                let countKey = '';
                if (section.label === 'Co Counsel') {
                    if (status === 'Paid') countKey = 'secondaryFirmsCoCounselPaidCount';
                    else if (status === 'NotStarted') countKey = 'secondaryFirmsCoCounselNotStartedCount';
                    else if (status === 'Failed') countKey = 'secondaryFirmsCoCounselFailedCount';
                    else if (status === 'Funding') countKey = 'secondaryFirmsCoCounselFundingCount';
                    else if (status === 'InDistribution') countKey = 'secondaryFirmsCoCounselInDistributionCount';
                } else if (section.label === 'Referring Counsel') {
                    if (status === 'Paid') countKey = 'secondaryFirmsReferingCounselPaidCount';
                    else if (status === 'NotStarted') countKey = 'secondaryFirmsReferingCounselNotStartedCount';
                    else if (status === 'Failed') countKey = 'secondaryFirmsReferingCounselFailedCount';
                    else if (status === 'Funding') countKey = 'secondaryFirmsReferingCounselFundingCount';
                    else if (status === 'InDistribution') countKey = 'secondaryFirmsReferingCounselInDistributionCount';
                }
                const count = attorneysFees[countKey] || 0;
                return (
                    <Box key={key} className={classes.row}>
                        <Typography className={classes.label}>
                            {section.label} - {count} items
                        </Typography>
                        <Typography className={classes.amountCenter}>
                            {numberToUSDCurrency(attorneysFees[key])}
                        </Typography>
                        {status &&
                            <Box className={classes.statusBadge}>
                                <StatusChip
                                    label={AttorneysFeeStatusMap[status as AttorneysFeeStatus].label || status}
                                    color={AttorneysFeeStatusMap[status as AttorneysFeeStatus].color || 'default'}
                                />
                            </Box>}
                    </Box>
                );
            })
    );
};
